import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Text,
  Group,
  Badge,
  Loader,
  Alert,
  Table,
  ScrollArea,
  Card,
  SimpleGrid,
  Accordion,
  ThemeIcon,
} from '@mantine/core';
import { IconAlertCircle, IconCalendar, IconSun, IconMoon } from '@tabler/icons-react';
import { useGetHourlyData, type HourlyDataPoint } from '../../hooks/useGetHourlyData';

interface MonthDetailModalProps {
  opened: boolean;
  onClose: () => void;
  yearMonth: string;
  monthName: string;
}

// // const _MONTH_NAMES = [
//   'Styczeń',
//   'Luty',
//   'Marzec',
//   'Kwiecień',
//   'Maj',
//   'Czerwiec',
//   'Lipiec',
//   'Sierpień',
//   'Wrzesień',
//   'Październik',
//   'Listopad',
//   'Grudzień',
// ];

/**
 * Check if date is weekend or holiday
 */
function isWeekendOrHoliday(date: Date): boolean {
  const dayOfWeek = date.getDay();
  // 0 = Sunday, 6 = Saturday
  return dayOfWeek === 0 || dayOfWeek === 6;
  // TODO: Add Polish holidays check if needed
}

/**
 * Get tariff zone based on hour, tariff type, and day type
 * @param hour - Hour of day (0-23)
 * @param tariffType - Tariff type (G11, G12, G12w, G13, G13s)
 * @param isWeekend - Whether the day is weekend or holiday
 * @returns Tariff zone: 'peak', 'offpeak', or 'night'
 */
function getTariffZone(
  hour: number,
  tariffType: string | null,
  isWeekend: boolean
): 'peak' | 'offpeak' | 'night' {
  if (!tariffType) {
    return 'offpeak';
  }

  const tariff = tariffType.toUpperCase();

  // G11: Single zone (no peak/offpeak distinction)
  if (tariff === 'G11') {
    return 'offpeak';
  }

  // G12: Two zones (same for weekdays and weekends)
  // Peak: 6-13, 19-22
  // Offpeak: rest
  if (tariff === 'G12') {
    if ((hour >= 6 && hour < 13) || (hour >= 19 && hour < 22)) {
      return 'peak';
    }
    return 'offpeak';
  }

  // G12w: Two zones (weekend different)
  // Weekdays: Peak 6-13, 19-22 | Offpeak rest
  // Weekend: All offpeak
  if (tariff === 'G12W') {
    if (isWeekend) {
      return 'offpeak';
    }
    if ((hour >= 6 && hour < 13) || (hour >= 19 && hour < 22)) {
      return 'peak';
    }
    return 'offpeak';
  }

  // G13: Three zones (weekend different)
  // Weekdays: Peak 7-13, 19-22 | Offpeak 13-19 | Night 22-7
  // Weekend: Offpeak 7-22 | Night 22-7
  if (tariff === 'G13') {
    if (isWeekend) {
      // Weekend: no peak, only offpeak and night
      if (hour >= 22 || hour < 7) {
        return 'night';
      }
      return 'offpeak';
    }
    // Weekday
    if ((hour >= 7 && hour < 13) || (hour >= 19 && hour < 22)) {
      return 'peak';
    }
    if (hour >= 22 || hour < 7) {
      return 'night';
    }
    return 'offpeak';
  }

  // G13s: Three zones (weekend different for peak only)
  // Weekdays: Peak 7-13, 19-22 | Offpeak 13-19 | Night 0-7, 22-24
  // Weekend: No peak (all offpeak or night) | Night 0-7, 22-24
  if (tariff === 'G13S') {
    // Night zone: 0-7, 22-24 (same for weekdays and weekends)
    if (hour >= 22 || hour < 7) {
      return 'night';
    }
    // Peak only on weekdays
    if (!isWeekend && ((hour >= 7 && hour < 13) || (hour >= 19 && hour < 22))) {
      return 'peak';
    }
    return 'offpeak';
  }

  // Default fallback
  return 'offpeak';
}

/**
 * Group hourly data by day
 */
function groupByDay(hourlyData: HourlyDataPoint[]) {
  const days: { [day: number]: HourlyDataPoint[] } = {};

  hourlyData.forEach((point) => {
    const date = new Date(point.timestamp);
    const day = date.getDate();

    if (!days[day]) {
      days[day] = [];
    }
    days[day].push(point);
  });

  return days;
}

export function MonthDetailModal({ opened, onClose, yearMonth, monthName }: MonthDetailModalProps) {
  const { getHourlyData, loading, error } = useGetHourlyData();
  const [data, setData] = useState<Awaited<ReturnType<typeof getHourlyData>> | null>(null);

  useEffect(() => {
    if (opened && yearMonth) {
      getHourlyData(yearMonth)
        .then((result) => setData(result))
    }
  }, [opened, yearMonth, getHourlyData]);

  if (loading) {
    return (
      <Modal opened={opened} onClose={onClose} size="xl" title={`Szczegóły: ${monthName}`}>
        <Group justify="center" py="xl">
          <Loader size="lg" />
          <Text c="dimmed">Ładowanie danych godzinowych...</Text>
        </Group>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal opened={opened} onClose={onClose} size="xl" title={`Szczegóły: ${monthName}`}>
        <Alert color="red" icon={<IconAlertCircle size={18} />} title="Błąd ładowania">
          <Text size="sm">{error}</Text>
        </Alert>
      </Modal>
    );
  }

  if (!data) {
    return null;
  }

  const dayGroups = groupByDay(data.hourlyData);
  const days = Object.keys(dayGroups)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <Modal opened={opened} onClose={onClose} size="xl" title={`Szczegóły: ${monthName}`}>
      <Stack gap="md">
        {/* Summary */}
        <Card withBorder>
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
            <div>
              <Text size="xs" c="dimmed">
                Całkowite zużycie
              </Text>
              <Text size="lg" fw={600}>
                {data.totalConsumption.toLocaleString('pl-PL', {
                  maximumFractionDigits: 2,
                })}{' '}
                kWh
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">
                Średnia dzienna
              </Text>
              <Text size="lg" fw={600}>
                {data.avgDailyConsumption.toLocaleString('pl-PL', {
                  maximumFractionDigits: 2,
                })}{' '}
                kWh
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">
                Zaimportowane godziny
              </Text>
              <Text size="lg" fw={600}>
                {data.importedHoursCount} / {data.hourlyData.length}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">
                Taryfa
              </Text>
              <Badge size="lg" color={data.isExtrapolated ? 'orange' : 'green'} variant="light">
                {data.tariffType || 'Nieznana'}
              </Badge>
            </div>
          </SimpleGrid>
        </Card>

        {/* Daily Breakdown */}
        <div>
          <Text size="sm" fw={600} mb="xs">
            Rozbicie dzienne
          </Text>
          <Accordion variant="contained">
            {days.map((day) => {
              const dayData = dayGroups[day];
              const dailyTotal = dayData.reduce((sum, point) => sum + point.consumption, 0);
              const date = new Date(dayData[0].timestamp);
              const dayName = date.toLocaleDateString('pl-PL', { weekday: 'long' });

              return (
                <Accordion.Item key={day} value={`day-${day}`}>
                  <Accordion.Control>
                    <Group justify="space-between" wrap="nowrap">
                      <Group gap="xs">
                        <ThemeIcon size="sm" radius="xl" variant="light" color="blue">
                          <IconCalendar size={14} />
                        </ThemeIcon>
                        <div>
                          <Text size="sm" fw={500}>
                            {day} {monthName} - {dayName}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {(() => {
                              const importedHours = dayData.filter((h) => h.isImported).length;
                              const estimatedHours = dayData.length - importedHours;
                              if (estimatedHours === 0) {
                                return (
                                  <Text component="span" c="green" inherit>
                                    zaimportowano {importedHours} h
                                  </Text>
                                );
                              }
                              if (importedHours === 0) {
                                return (
                                  <Text component="span" c="orange" inherit>
                                    oszacowano {estimatedHours} h
                                  </Text>
                                );
                              }
                              return (
                                <>
                                  <Text component="span" c="green" inherit>
                                    zaimportowano {importedHours} h
                                  </Text>
                                  {', '}
                                  <Text component="span" c="orange" inherit>
                                    oszacowano {estimatedHours} h
                                  </Text>
                                </>
                              );
                            })()}
                          </Text>
                        </div>
                      </Group>
                      <Text size="sm" fw={600}>
                        {dailyTotal.toLocaleString('pl-PL', {
                          maximumFractionDigits: 2,
                        })}{' '}
                        kWh
                      </Text>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <ScrollArea h={300}>
                      <Table striped highlightOnHover withTableBorder withColumnBorders>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Godzina</Table.Th>
                            <Table.Th>Zużycie (kWh)</Table.Th>
                            <Table.Th>Strefa taryfowa</Table.Th>
                            <Table.Th>Status</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {dayData.map((point) => {
                            const date = new Date(point.timestamp);
                            const hour = date.getHours();
                            const isWeekend = isWeekendOrHoliday(date);
                            const zone = getTariffZone(hour, data.tariffType, isWeekend);

                            // Badge color based on zone
                            const zoneColor = zone === 'peak' ? 'red' : zone === 'night' ? 'indigo' : 'green';
                            const zoneLabel = zone === 'peak' ? 'Szczyt' : zone === 'night' ? 'Noc' : 'Pozaszczyt';

                            return (
                              <Table.Tr key={point.hour}>
                                <Table.Td>
                                  <Group gap="xs">
                                    <ThemeIcon
                                      size="xs"
                                      radius="xl"
                                      variant="light"
                                      color={hour >= 6 && hour < 22 ? 'yellow' : 'indigo'}
                                    >
                                      {hour >= 6 && hour < 22 ? (
                                        <IconSun size={10} />
                                      ) : (
                                        <IconMoon size={10} />
                                      )}
                                    </ThemeIcon>
                                    <Text size="sm">
                                      {hour.toString().padStart(2, '0')}:00 -{' '}
                                      {(hour + 1).toString().padStart(2, '0')}:00
                                    </Text>
                                  </Group>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm" fw={500}>
                                    {point.consumption.toLocaleString('pl-PL', {
                                      maximumFractionDigits: 3,
                                    })}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Badge size="sm" color={zoneColor} variant="light">
                                    {zoneLabel}
                                  </Badge>
                                </Table.Td>
                                <Table.Td>
                                  <Badge
                                    size="sm"
                                    color={point.isImported ? 'blue' : 'orange'}
                                    variant="light"
                                  >
                                    {point.isImported ? 'Import' : 'Oszacowanie'}
                                  </Badge>
                                </Table.Td>
                              </Table.Tr>
                            );
                          })}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                  </Accordion.Panel>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </div>
      </Stack>
    </Modal>
  );
}
